'use client';

import {
	BigNum,
	BN,
	getTokenAmount,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import { BankBalanceUI, COMMON_UI_UTILS, MarketId } from '@drift/common';
import useDriftClient from './useDriftClient';
import useDriftClientIsReady from './useDriftClientIsReady';
import useSubaccountCollateralBalances from './useSubaccountCollateralBalances';
import { useEffect, useMemo, useRef } from 'react';
import useDriftAccountStore, {
	AccountSpotBalance,
} from 'src/stores/useDriftAccountsStore';
import useGetOraclePriceForMarket from './useGetOraclePriceForMarket';
import { useSpotMarketsSortedByMarketIndex } from './spotMarkets/useSpotMarketsSortedByMarketIndex';
import { useAllPricingData } from 'src/hooks/query/pricing/usePricingData';

const checkIfBalancesAreEqual = (balances1: BN[], balances2: BN[]): boolean => {
	if (balances1.length !== balances2.length) {
		return false;
	}

	return balances1.every((balance1, index) => {
		return balance1.eq(balances2[index]);
	});
};

export const useSyncAccountSpotBalances = () => {
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftClient();
	const setAccountsStore = useDriftAccountStore((s) => s.set);
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();

	const previousUserBankBalances = useRef<BankBalanceUI[]>([]);
	const userBankBalances = useSubaccountCollateralBalances(
		currentUserKey,
		true
	);

	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const { data: allPricingData } = useAllPricingData();

	// Get the unique market indices from user's balances
	const userMarketIndices = useMemo(() => {
		const balancesToCheck =
			userBankBalances.length > 0
				? userBankBalances
				: previousUserBankBalances.current;
		return [...new Set(balancesToCheck.map((b) => b.marketIndex))];
	}, [userBankBalances]);

	// Build a change-detection key from pricing data oracle prices for the user's markets
	const pricingDataPricesKey = useMemo(() => {
		if (userMarketIndices.length === 0) return '';
		return userMarketIndices
			.map((idx) => {
				const oraclePrice = allPricingData?.get(
					MarketId.createSpotMarket(idx).key
				)?.oraclePrice;
				return `${idx}:${oraclePrice ?? ''}`;
			})
			.join(',');
	}, [userMarketIndices, allPricingData]);

	// Use getOraclePriceForMarket to get the best price (highest slot from all sources)
	// This recalculates when pricing data updates prices for the user's markets
	const userMarketsOraclePricesKey = useMemo(() => {
		if (userMarketIndices.length === 0) return '';
		return userMarketIndices
			.map((marketIndex) => {
				const marketId = MarketId.createSpotMarket(marketIndex);
				const price = getOraclePriceForMarket(marketId);
				return `${marketIndex}:${price?.toString() ?? '0'}`;
			})
			.join(',');
	}, [userMarketIndices, pricingDataPricesKey, getOraclePriceForMarket]);

	// Memoised hook to make sure that we only format and update bank balances if there is actually a change in the balances
	const newBankBalances: BankBalanceUI[] = useMemo(() => {
		if (!driftClientIsReady || !driftClient || !currentUserKey) return [];

		const bankBalancesInStore =
			useDriftAccountStore.getState().accounts[currentUserKey]?.spotBalances;

		const balancesAreEqual = bankBalancesInStore
			? checkIfBalancesAreEqual(
					bankBalancesInStore.map((b) => b.balance.val),
					userBankBalances.map((b) => b.scaledBalance)
				)
			: false;

		if (balancesAreEqual) {
			return previousUserBankBalances.current;
		}

		previousUserBankBalances.current = userBankBalances;
		return userBankBalances;
	}, [driftClientIsReady, driftClient, userBankBalances]);

	// Sync when balances change OR when oracle prices for user's markets change
	useEffect(() => {
		if (!driftClientIsReady || !driftClient) return;

		const formattedSpotBalances = formatSpotBalances(
			newBankBalances.length > 0
				? newBankBalances
				: previousUserBankBalances.current
		);

		setSpotBalancesInStore(
			formattedSpotBalances.filter(Boolean) as AccountSpotBalance[]
		);
	}, [
		driftClientIsReady,
		driftClient,
		newBankBalances,
		userMarketsOraclePricesKey,
	]);

	const formatSpotBalances = (userBankBalances: BankBalanceUI[]) => {
		if (spotMarketsSortedByMarketIndex.length === 0) return [];
		return userBankBalances
			.map((bankBalance) => {
				const spotAccount = driftClient?.getSpotMarketAccount(
					bankBalance.marketIndex
				);
				if (
					!spotMarketsSortedByMarketIndex[bankBalance.marketIndex] ||
					!spotAccount
				)
					return null;
				const assetPrecision =
					spotMarketsSortedByMarketIndex[bankBalance.marketIndex].precisionExp;

				const baseBalance = BigNum.from(
					getTokenAmount(
						bankBalance.scaledBalance,
						spotAccount,
						bankBalance.balanceType
					),
					assetPrecision
				).shiftTo(assetPrecision);

				const marketId = MarketId.createSpotMarket(bankBalance.marketIndex);

				const oraclePrice = getOraclePriceForMarket(marketId);
				if (!oraclePrice) return null;

				const quoteValue = baseBalance
					.shiftTo(PRICE_PRECISION_EXP)
					.mul(oraclePrice)
					.shiftTo(QUOTE_PRECISION_EXP);

				return {
					asset: spotMarketsSortedByMarketIndex[bankBalance.marketIndex],
					balance: baseBalance,
					balanceType: bankBalance.balanceType,
					quoteValue,
					accountId: bankBalance.accountId,
					accountName: bankBalance.accountName,
					accountKey: COMMON_UI_UTILS.getUserKey(
						bankBalance.accountId,
						bankBalance.accountAuthority
					),
				};
			})
			.filter(Boolean);
	};

	const setSpotBalancesInStore = (spotBalances: AccountSpotBalance[]) => {
		const groupedAccountsBalances = spotBalances.reduce<
			Record<string, AccountSpotBalance[]>
		>((acc, spotBalance) => {
			const { accountKey } = spotBalance;

			if (!acc[accountKey]) {
				acc[accountKey] = [];
			}

			acc[accountKey].push(spotBalance);

			return acc;
		}, {});

		setAccountsStore((s) => {
			const accountKeys = Object.keys(s.accounts);
			accountKeys.forEach((accountKey) => {
				if (!s.accounts[accountKey]) return;

				s.accounts[accountKey].spotBalances =
					groupedAccountsBalances[accountKey] ?? [];
			});
		});
	};
};
